import styled, { css, keyframes } from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import { COLORS, GRADIENTS, ANIM, MOBILE_MQ } from '../../../../theme/tokens';
import type { DeviceMode } from '../../live-preview';

/* Live in-website preview — the brand's real site embedded in an iframe, with the owner able to
   click a banner in it (via the creative-bridge postMessage contract) to edit that banner. The
   stage is a rounded, hairline-framed surface; the iframe fades in once the site announces its
   paint (or a settle timeout), over a shimmering placeholder. */

const shimmer = keyframes`
  0%   { background-position: -480px 0; }
  100% { background-position: 480px 0; }
`;

export const Stage = styled.div<{ $fill?: boolean; $device?: DeviceMode }>`
  position: relative;
  width: 100%;
  /* $fill: fill the host (the big preview modal); otherwise a fixed inline preview height. */
  height: ${({ $fill }) => ($fill ? '100%' : '460px')};
  border-radius: ${({ $fill }) => ($fill ? '0' : '16px')};
  overflow: hidden;
  /* A constrained device (tablet/mobile) floats over a darker backdrop; desktop keeps the card. */
  background: ${({ $device }) => ($device && $device !== 'desktop' ? COLORS.pageBg : COLORS.surfaceCard)};
  border: ${({ $fill }) => ($fill ? '0' : `1px solid ${COLORS.hairline}`)};

  @media ${MOBILE_MQ} {
    height: ${({ $fill }) => ($fill ? '100%' : '320px')};
  }
`;

/* The framed device viewport: fills the stage on desktop, or a centred fixed-width column on
   tablet / mobile. The iframe + hover overlay both live inside it, so a banner's reported rect (in the
   iframe's own viewport coords) still maps 1:1 onto the overlay at any device width. */
export const DeviceViewport = styled.div<{ $width: number | null }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: ${({ $width }) => ($width ? `${$width}px` : '100%')};
  max-width: 100%;
  overflow: hidden;
  background: ${COLORS.surfaceCard};
  transition: width ${ANIM.DRAWER_MS}ms ${ANIM.EASE};
  border-left: ${({ $width }) => ($width ? `1px solid ${COLORS.hairline}` : '0')};
  border-right: ${({ $width }) => ($width ? `1px solid ${COLORS.hairline}` : '0')};
`;

export const Frame = styled.iframe<{ $revealed: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
  opacity: ${({ $revealed }) => ($revealed ? 1 : 0)};
  transition: opacity ${ANIM.DRAWER_MS}ms ${ANIM.EASE};
`;

export const Loading = styled.div<{ $hidden: boolean }>`
  position: absolute;
  inset: 0;
  background-image: ${GRADIENTS.barSheen};
  background-size: 480px 100%;
  background-repeat: no-repeat;
  background-color: ${COLORS.surfaceFaint};
  animation: ${shimmer} 1200ms ${ANIM.EASE} infinite;
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  transition: opacity ${ANIM.DRAWER_MS}ms ${ANIM.EASE};
  pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};
`;

/* The floating hint that tells the owner the site is interactive here. */
export const Hint = styled.div`
  position: absolute;
  left: 50%;
  bottom: 14px;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  background: ${GRADIENTS.chipShade};
  border: 1px solid ${COLORS.hairline};
  color: ${COLORS.text};
  box-shadow: 0 8px 24px -8px ${COLORS.pageBg};
  pointer-events: none;
  max-width: calc(100% - 28px);
`;

export const HintText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 500;
  line-height: 15px;
  color: ${COLORS.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* The click-to-edit halo drawn over a hovered banner. The bridge reports the banner's rect (in the
   iframe's own viewport coords, which map 1:1 onto the Stage because the frame is inset:0), and we
   position this box there. pointer-events:none keeps hover + click flowing through to the bridge. */
const haloIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const haloBox = css`
  position: absolute;
  z-index: 2;
  box-sizing: border-box;
  /* Dark scrim over the banner with the "Click to edit" chip centred inside it — the banner reads as
     dimmed/actionable and the chip is the focal cue. */
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${GRADIENTS.chipShade};
  border: 1.5px solid ${COLORS.accent};
  border-radius: 10px;
  animation: ${haloIn} ${ANIM.INTERACT_MS}ms ${ANIM.EASE} both;
`;

export const EditHalo = styled.div`
  ${haloBox}
  pointer-events: none;
`;

/* "Click to edit" chip centred over the shaded banner, carrying the Creatives primary colour so it
   reads as the same action as the publish/edit affordances elsewhere. */
export const EditPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 13px;
  border-radius: 999px;
  background: ${COLORS.publishBlue};
  color: ${COLORS.ctaSolidText};
  box-shadow: 0 8px 22px -8px ${COLORS.pageBg};
  white-space: nowrap;
`;

export const EditPillText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  font-weight: 650;
  line-height: 13px;
  color: ${COLORS.ctaSolidText};
`;
